import fetchVendors from '@/src/utils/vendors/fetchVendors';
import { useEffect, useRef, useState } from 'react';

function useGetVendors(
  landingId,
  countryId,
  latitude,
  longitude,
  landingType,
  filter,
  sorting,
  filtersMetadata,
  headers,
  initialVendors = [],
  initialTotalVendors = 0,
  initialSize = 0,
  initialOriginalPeyaTrace = '',
  initialMidasRequestId = '',
  isMigration = false,
) {
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(initialVendors.length === 0);
  const [vendorError, setVendorError] = useState(undefined);
  const [vendors, setVendors] = useState(initialVendors);
  const [totalVendors, setTotalVendors] = useState(
    initialVendors.length > 0 ? initialTotalVendors : null,
  );
  const [midasRequestId, setMidasRequestId] = useState(initialMidasRequestId);
  const [originalPeyaTrace, setOriginalPeyaTrace] = useState(
    initialOriginalPeyaTrace,
  );
  const [size, setSize] = useState(initialSize);

  // Cache for initial data (migration flow)
  const initialData = useRef({
    vendors: initialVendors,
    total: initialTotalVendors,
    size: initialSize,
    midasRequestId: initialMidasRequestId,
    originalPeyaTrace: initialOriginalPeyaTrace,
  });

  const getVendors = async () => {
    try {
      if (totalVendors > 0 && vendors.length >= totalVendors) {
        setLoading(false);
        return;
      }

      // If we are in migration mode, page 1 and returning to the initial filter/sorting
      // we restore from cache instead of fetching.
      const isInitialFilter = !filter || filter === filtersMetadata?.main;
      if (isMigration && page === 1 && isInitialFilter && !sorting) {
        const { total } = initialData.current;
        setVendors(initialData.current.vendors);
        setTotalVendors(total);
        if (total === 0) {
          setVendorError(404);
        }
        setMidasRequestId(initialData.current.midasRequestId);
        setOriginalPeyaTrace(initialData.current.originalPeyaTrace);
        setSize(initialData.current.size);
        setLoading(false);
        return;
      }

      setLoading(true);
      const { data, error } = await fetchVendors(
        page,
        landingId,
        countryId,
        latitude,
        longitude,
        landingType,
        filter,
        sorting,
        filtersMetadata,
        headers,
      );
      if (error) {
        if (error?.response?.status) {
          setVendorError(error.response.status);
        } else if (error === 'stop_load_vendors') {
          setVendorError(error);
        } else if (error?.code === 'ECONNABORTED') {
          // error by timeout
          setVendorError('ECONNABORTED');
        } else if (typeof error === 'number') {
          setVendorError(error);
        } else {
          setVendorError(500);
        }
      } else if (data && data.vendors.length > 0) {
        if (data.vendors) {
          setVendors([...vendors, ...data.vendors]);
          setTotalVendors(data.pagination.total);
          setMidasRequestId(data.metadata.midas_request_id);
          setOriginalPeyaTrace(data.metadata?.original_peya_trace_id);
          setSize(data?.pagination.size);
          setVendorError(undefined);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (loading) {
      getVendors();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  return {
    loading,
    vendorError,
    vendors,
    totalVendors,
    setVendorError,
    setVendors,
    setTotalVendors,
    midasRequestId,
    page,
    setPage,
    setLoading,
    size,
    originalPeyaTrace,
  };
}

export default useGetVendors;
